import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Menu, X } from 'lucide-react';
import { profileData } from '../../data/profileData';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import { Icon } from '../ui';
import './Navigation.css';

interface NavigationProps {
  activeSection: string;
  scrollToSection: (sectionId: string) => void;
}

const Navigation: React.FC<NavigationProps> = ({ activeSection, scrollToSection }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const progressBarRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    let ticking = false;
    const handleScroll = (): void => {
      if (ticking) return;
      window.requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 16);

        // Update progress bar width via DOM ref — no re-render
        if (progressBarRef.current) {
          const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
          const progress = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
          progressBarRef.current.style.width = `${Math.min(progress, 100)}%`;
        }
        ticking = false;
      });
      ticking = true;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close the mobile menu on an outside click or Escape (returning focus to the toggle).
  const handleOutsideClick = useCallback((e: MouseEvent) => {
    if (navRef.current && !navRef.current.contains(e.target as Node)) {
      setIsMobileMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen, handleOutsideClick]);

  const handleNavClick = (sectionId: string): void => {
    scrollToSection(sectionId);
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = (): void => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  return (
    <nav ref={navRef} className={`navbar${isScrolled ? ' scrolled' : ''}`} aria-label="Primary">
      <div ref={progressBarRef} className="scroll-progress" style={{ width: '0%' }} aria-hidden="true" />
      <div className="nav-container">
        <button className="nav-logo" onClick={handleLogoClick} aria-label={`${profileData.basicInfo.name}, back to top`}>
          {profileData.basicInfo.name}
        </button>

        <ul id="nav-menu" className={`nav-menu${isMobileMenuOpen ? ' mobile-open' : ''}`}>
          {profileData.navigation.map((item) => {
            const isActive = activeSection === item.id;
            return (
              <li key={item.id}>
                <button
                  className={`nav-link${isActive ? ' active' : ''}`}
                  onClick={() => handleNavClick(item.id)}
                  aria-current={isActive ? 'location' : undefined}
                >
                  {item.label}
                </button>
              </li>
            );
          })}
        </ul>

        <div className="nav-actions">
          <ThemeToggle />
          <button
            ref={toggleRef}
            className="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="nav-menu"
          >
            <Icon icon={isMobileMenuOpen ? X : Menu} size={22} />
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
